import { useEffect, useRef, useState } from 'react';
import {
  DeviceAction,
  facilitatorsService,
  Management,
} from '../services/facilitatorsService';

const isEndedError = (error: unknown) => {
  if (!(error instanceof Error) || !error.message.startsWith('API Error: '))
    return false;
  try {
    const response = JSON.parse(error.message.slice('API Error: '.length));
    return (
      response.status === 404 ||
      response.status === 410 ||
      response.data?.code === 404 ||
      response.data?.code === 410 ||
      response.data?.message === 'Session has ended'
    );
  } catch {
    return false;
  }
};

export default function useSessionFacilitators(
  sessionId: number | null,
  isOpen: boolean
) {
  const [management, setManagement] = useState<Management | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const actions = useRef<{
    refresh: () => void;
    mutate: (
      method: DeviceAction | 'setLinkEnabled' | 'regenerateLink',
      value?: number | boolean
    ) => Promise<void>;
  } | null>(null);

  useEffect(() => {
    setManagement(null);
    setError(null);
    setEnded(false);
    setBusy(false);
    setLoading(isOpen && sessionId !== null && navigator.onLine);
    setOffline(!navigator.onLine);
    if (!isOpen || sessionId === null) return undefined;
    let disposed = false;
    let requestId = 0;
    let controller: AbortController | null = null;
    let mutating = false;
    let fetching = false;
    let closed = false;
    const current = (id: number) => !disposed && id === requestId;
    const accept = (result: Management) => {
      if (result.sessionId !== sessionId) return;
      setManagement(result);
      setError(null);
    };
    const fail = (failure: unknown) => {
      if (isEndedError(failure)) {
        closed = true;
        setEnded(true);
        setManagement(null);
      } else {
        setError('Could not update facilitator access. Please retry.');
      }
    };
    const refresh = async () => {
      if (
        disposed ||
        mutating ||
        fetching ||
        closed ||
        !navigator.onLine ||
        document.hidden
      )
        return;
      fetching = true;
      controller?.abort();
      controller = new AbortController();
      requestId += 1;
      const id = requestId;
      try {
        const result = await facilitatorsService.getManagement(
          { sessionId },
          controller.signal
        );
        if (current(id)) accept(result);
      } catch (failure) {
        if (current(id)) fail(failure);
      } finally {
        if (current(id)) {
          fetching = false;
          setLoading(false);
        }
      }
    };
    const mutate = async (
      method: DeviceAction | 'setLinkEnabled' | 'regenerateLink',
      value?: number | boolean
    ) => {
      if (disposed || mutating || closed || !navigator.onLine) return;
      mutating = true;
      setBusy(true);
      setError(null);
      controller?.abort();
      fetching = false;
      controller = new AbortController();
      requestId += 1;
      const id = requestId;
      try {
        let result: Management;
        if (method === 'setLinkEnabled') {
          result = await facilitatorsService.setLinkEnabled(
            { sessionId, enabled: value === true },
            controller.signal
          );
        } else if (method === 'regenerateLink') {
          result = await facilitatorsService.regenerateLink(
            { sessionId },
            controller.signal
          );
        } else {
          result = await facilitatorsService[method](
            { sessionId, deviceId: Number(value) },
            controller.signal
          );
        }
        if (current(id)) accept(result);
      } catch (failure) {
        if (current(id)) fail(failure);
      } finally {
        if (current(id)) {
          mutating = false;
          setBusy(false);
        }
      }
    };
    actions.current = { refresh, mutate };
    const online = () => {
      setOffline(false);
      refresh();
    };
    const disconnected = () => {
      setOffline(true);
      controller?.abort();
      requestId += 1;
      mutating = false;
      fetching = false;
      setBusy(false);
      setLoading(false);
    };
    const interval = window.setInterval(refresh, 2000);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', online);
    window.addEventListener('offline', disconnected);
    document.addEventListener('visibilitychange', refresh);
    refresh();
    return () => {
      disposed = true;
      requestId += 1;
      controller?.abort();
      actions.current = null;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', disconnected);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [sessionId, isOpen]);

  return {
    management:
      isOpen && management?.sessionId === sessionId ? management : null,
    loading,
    busy,
    error,
    ended,
    offline,
    refresh: () => actions.current?.refresh(),
    mutate: (
      method: DeviceAction | 'setLinkEnabled' | 'regenerateLink',
      value?: number | boolean
    ) => actions.current?.mutate(method, value),
  };
}
